<script lang="ts">
	import { getLuxAiController } from "../LuxAIController.svelte"
	import { Responsive } from "responsive"
	import { Models as M } from "@lux-ai/api-interface"
	import { LoadingSection } from "misc-ui"
	import LuxStudioIcon from "../icons/LuxStudioIcon.svelte"
	import SubscribeCard from "./SubscribeCard.svelte"
	import ActiveGenerations from "./active-generations/ActiveGenerations.svelte"
	import ThemeCollection from "./themes/ThemeCollection.svelte"
	import { ErrorIndicator } from "luxedo-comps"

	const controller = getLuxAiController()

	let collections = $derived(controller.availableCollections)
	let activeGenerations = $derived(controller.activeGenerations)

	let canAccessPrivateThemes = $derived(
		controller.userDataGetter.getCurrentUserData().isAISuperUser,
	)

	let isUserSubscribed = $derived(controller.userDataGetter.getCurrentUserData().isSubscribed)

	let freeThemes = $derived(controller.availableThemes.filter((t) => t.free))
	let privateThemes = $derived(controller.availableThemes.filter((t) => !t.public))

	function filterThemesById(ids: Array<M.Theme["id"]>, themes: Array<M.Theme>) {
		return themes.filter((t) => ids.includes(t.id))
	}
</script>

<div id="lux-ai-gallery" class:mobile={Responsive.isMobile}>
	<div class="heading">
		<div class="icon-wrapper">
			<LuxStudioIcon strokeColor="var(--color-text-light)" />
		</div>

		<p class="info">
			Experience the magic of AI-driven customization as our advanced algorithms transform carefully
			curated themes into personalized visual experiences. By analyzing your projection space's
			unique characteristics, we create content that's perfectly mapped to your environment -
			turning any space into an immersive, tailored display that's unlike anything else.
		</p>
	</div>
	{#await controller.loadInitialData()}
		<LoadingSection textContent="Loading themes" />
	{:then}
		{#if activeGenerations.length}
			<ActiveGenerations {activeGenerations} />
		{/if}

		<ThemeCollection
			themes={freeThemes}
			title={"Free Themes"}
			description={`Welcome to LuxStudio! You can get started with these free themes to see how LuxStudio can completely transform your home in minutes. Click "Generate" on any theme to get started!`}
			{activeGenerations}
		/>

		{#if !isUserSubscribed && !canAccessPrivateThemes}
			<SubscribeCard onSubscribe={controller.quickActions.jumpToSubscribe} />
		{/if}

		{#if canAccessPrivateThemes}
			<ThemeCollection
				themes={privateThemes}
				title={"Private Themes"}
				description={`As an AI superuser or admin, you have access to these unreleased experimental themes.`}
				{activeGenerations}
			/>
		{/if}

		{#each collections as collection}
			<ThemeCollection
				title={`${collection.name} Collection`}
				{activeGenerations}
				themes={filterThemesById(collection.themes, controller.availableThemes)}
			/>
		{/each}
	{:catch}
		<ErrorIndicator
			errorMessage="Failed to load LuxStudio, please refresh and try again."
			linkToSupport
		/>
	{/await}
</div>

<style>
	#lux-ai-gallery {
		overflow-y: scroll;
		padding: 0 0.5rem;
		flex-grow: 1;
		display: flex;
		flex-direction: column;
	}

	#lux-ai-gallery :global(.Loading) {
		flex-grow: 1;
	}

	.heading {
		align-self: center;
	}

	.info {
		color: var(--color-text);
	}

	.icon-wrapper {
		max-height: 4rem;
		position: relative;
		display: flex;
		flex-direction: column;
	}

	.icon-wrapper :global(svg) {
		height: 100%;
	}

	#lux-ai-gallery :global(.collection-title) {
		margin-top: 1rem;
		margin-bottom: 0;
		font-size: var(--text-h4);
		color: var(--color-main);
	}

	#lux-ai-gallery :global(.theme-collection) {
		margin: 0 0 2rem 0;
	}

	#lux-ai-gallery :global(.themes) {
		container-type: inline-size;
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		justify-content: flex-start;
		gap: 1rem;

		max-height: 100%;
		overflow-y: auto;
	}

	#lux-ai-gallery :global(.section-info) {
		margin-top: 0px;
		line-height: 1.4em;
		color: var(--color-text);
	}

	#lux-ai-gallery.mobile :global(.collection-title) {
		margin-top: 1rem;
		text-align: center;
	}

	#lux-ai-gallery.mobile :global(.section-info) {
		text-align: center;
	}
</style>
